import { FormEvent, useState } from "react";
import { useQuote } from "../contexts/QuoteContext";

export type VolumeRange = "<50" | "50-500" | "500-5000" | ">5000";
export type FormStatus = "idle" | "submitting" | "success" | "error";

export interface QuoteFormValues {
  company: string;
  name: string;
  email: string;
  phone: string;
  activity: string;
  country: string;
  volume: VolumeRange | "";
  samples: boolean;
  message: string;
  consent: boolean;
}

type Errors = Partial<Record<keyof QuoteFormValues, string>>;

const initialValues: QuoteFormValues = {
  company: "",
  name: "",
  email: "",
  phone: "",
  activity: "",
  country: "France",
  volume: "",
  samples: true,
  message: "",
  consent: false,
};

export function useQuoteForm() {
  const { items, clear } = useQuote();
  const [values, setValues] = useState<QuoteFormValues>(initialValues);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<FormStatus>("idle");

  function setField<K extends keyof QuoteFormValues>(key: K, value: QuoteFormValues[K]) {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate(): Errors {
    const e: Errors = {};
    if (!values.company.trim()) e.company = "Indiquez votre société.";
    if (!values.name.trim()) e.name = "Indiquez votre nom.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = "Adresse e-mail professionnelle invalide.";
    if (!values.activity) e.activity = "Sélectionnez votre activité.";
    if (items.length === 0 && !values.message.trim())
      e.message = "Ajoutez des ingrédients ou décrivez votre besoin.";
    if (!values.consent) e.consent = "Votre accord est nécessaire pour vous recontacter.";
    return e;
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const first = Object.keys(e)[0];
      document.getElementById(`q-${first}`)?.focus();
      return;
    }
    setStatus("submitting");
    try {
      await new Promise((r) => setTimeout(r, 1100));
      setStatus("success");
      clear();
    } catch {
      setStatus("error");
    }
  }

  function reset() {
    setValues(initialValues);
    setErrors({});
    setStatus("idle");
  }

  return { values, errors, status, setField, handleSubmit, reset, items };
}
